<template>
  <el-upload class="picture-uploader" drag action="" :show-file-list="false" :auto-upload="false" :accept="accept.join(',')"
    :before-upload="beforeUpload" :on-change="handleChange">
    <div v-if="modelValue" class="w-[148px] h-[148px] relative">
      <!-- 图片文件 -->
      <template v-if="fileUtils.isImageFile(modelValue)">
        <img :src="fileUtils.getImagePreviewUrl(modelValue)"
          class="w-full h-full object-contain max-w-full cursor-pointer" />
      </template>
      <!-- 非图片文件显示文件名 -->
      <template v-else>
        <div class="w-full h-full flex flex-col items-center justify-center p-2 text-blue-700">
          <AliIcon name="file" :size="iconSize" class="mb-1" />
          <span class="text-xs text-center truncate w-full" :title="fileUtils.getFileName(modelValue)">
            {{ fileUtils.getFileName(modelValue) }}
          </span>
        </div>
      </template>
      <!-- 操作按钮区域 - hover时显示 -->
      <div v-if="showDeleteButton || showPreviewButton" class="absolute top-1 right-1 flex items-center opacity-0 transition-opacity duration-300">
        <!-- 预览按钮 -->
        <el-button v-if="showPreviewButton" type="primary" size="small" circle @click.stop="handlePreview">
          <AliIcon name="eye"></AliIcon>
        </el-button>
        <!-- 删除按钮 -->
        <el-button v-if="showDeleteButton" type="danger" size="small" circle @click.stop="deleteFile">
          <AliIcon name="delete"></AliIcon>
        </el-button>
      </div>
    </div>
    <div v-else
      class="w-[148px] h-[148px] bg-[#fafafa] flex flex-col items-center justify-center gap-1 px-2 text-gray-500">
      <AliIcon name="upload" :size="uploadIconSize" />
      <span class="text-xs text-center text-gray-500" v-if="uploadText">{{uploadText}}</span>
      <span class="text-xs text-center text-gray-400">{{ tipText || `支持${getFormatTip()}格式，不超过${maxSize}MB` }}</span>
    </div>
  </el-upload>

  <!-- 图片预览对话框 -->
  <el-dialog v-model="dialogVisible" title="图片预览" width="98%" top="5vh" class="max-w-[800px]">
    <img :src="dialogImageUrl" class="preview-image w-full h-auto" alt="预览图片" />
  </el-dialog>
</template>

<script setup lang="ts">
  import { ref, computed } from 'vue';
  import { fileUtils } from '@/utils';
  import { getFormatTip as getCommonFormatTip, beforeUpload as commonBeforeUpload } from './uploadUtils';

  // 定义props
  interface Props {
    modelValue : File | string | null;
    accept ?: string[];
    maxSize ?: number;
    iconSize ?: number;
    uploadIconSize ?: number;
    uploadText ?: string;
    tipText ?: string;
    showDeleteButton ?: boolean;
    showPreviewButton ?: boolean;
  }

  const props = withDefaults(defineProps<Props>(), {
    modelValue: null,
    accept: () => ['.jpeg', '.jpg', '.png'],
    maxSize: 30,
    iconSize: 32,
    uploadIconSize: 26,
    tipText: '',
    showDeleteButton: true,
    showPreviewButton: true
  });

  // 定义emits
  const emit = defineEmits<{
    'update:modelValue' : [value: File | string | null];
    'change' : [file: File | string | null];
    'preview' : [file: File | string];
  }>();

  // 使用通用的文件格式提示文本函数
  const getFormatTip = () => getCommonFormatTip(props.accept);


  // 文件上传前校验 - 使用公共工具函数
    const beforeUpload = (file : File) => {
      return commonBeforeUpload(file, props.maxSize, props.accept, getFormatTip());
    };

  // 文件改变处理
  const handleChange = (file : any) => {
    const rawFile = file.raw || file;
    if (beforeUpload(rawFile)) {
      emit('update:modelValue', rawFile);
      emit('change', rawFile);
    } else {
      emit('update:modelValue', '');
      emit('change', '');
    }
  };

  // 删除文件
  const deleteFile = () => {
    emit('update:modelValue', '');
    emit('change', '');
  };

  // 预览弹窗相关状态
  const dialogVisible = ref(false);
  const dialogImageUrl = ref('');

  // 文件预览处理
  const handlePreview = () => {
    if (props.modelValue) {
      emit('preview', props.modelValue);

      // 根据文件类型处理预览
      if (fileUtils.isImageFile(props.modelValue)) {
        // 图片文件显示弹窗
        dialogImageUrl.value = fileUtils.getImagePreviewUrl(props.modelValue);
        dialogVisible.value = true;
      } else {
        fileUtils.downloadFile({
          file: props.modelValue
        });
      }
    }
  };
</script>

<style scoped>
  /* .picture-uploader ::v-deep(.el-upload) {
    border: 1px dashed var(--el-border-color);
    border-radius: 6px;
    cursor: pointer;
    position: relative;
    overflow: hidden;
    transition: var(--el-transition-duration-fast);
  }

  .picture-uploader ::v-deep(.el-upload:hover) {
    border-color: var(--el-color-primary);
  } */
  .picture-uploader ::v-deep(.el-upload-dragger) {
    padding: 0!important;
  }
  .picture-uploader ::v-deep(.el-upload-dragger.is-dragover) {
    border-width: 1px!important;
  }

  /* 文件预览容器hover时显示操作按钮 */
  .picture-uploader ::v-deep(.relative:hover) > div[class*="opacity-0"] {
    opacity: 1;
  }

  /* 预览图片样式 */
  .preview-image {
    max-height: 80vh;
    object-fit: contain;
  }
</style>